import { useEffect } from 'react'
import { matchPath, useLocation } from 'react-router-dom'
import {
    OG_IMAGE, PROD_ORIGIN, notFoundSeo, routeSeo, sharedResultSeo, sitewideJsonLd,
    type PageSeo,
} from '../content/seo'

const KNOWN_PATHS = Object.keys(routeSeo)

/** The metadata for the current location. Exact routes first, then the dynamic
    shared-result path, then a noindex fallback for anything unknown (404). */
function resolve(pathname: string): { seo: PageSeo; path: string } {
    if (routeSeo[pathname]) return { seo: routeSeo[pathname], path: pathname }
    if (matchPath('/dns-leak-test/:id', pathname)) return { seo: sharedResultSeo, path: pathname }
    return { seo: notFoundSeo, path: pathname }
}

/**
 * Update the existing <meta>/<link> for this key, or create one if absent.
 *
 * Matching does NOT require a marker: the static tags in index.html (canonical,
 * description, og:*, twitter:*) are updated in place, so there is exactly one of
 * each rather than a duplicate alongside the pre-hydration default. Two
 * canonicals or two og:urls would give search engines conflicting signals.
 */
function upsert(tag: 'meta' | 'link', attrs: Record<string, string>, keyAttr: string) {
    const selector = `${tag}[${keyAttr}="${attrs[keyAttr]}"]`
    let el = document.head.querySelector<HTMLElement>(selector)
    if (!el) {
        el = document.createElement(tag)
        document.head.appendChild(el)
    }
    for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v)
}

/**
 * Clear every ld+json -- the static one from index.html and any graph we wrote
 * before -- and emit one authoritative graph, so the Organization and WebSite
 * nodes are never duplicated across two scripts.
 */
function emitJsonLd(pageBlocks: Record<string, unknown>[]) {
    document.head.querySelectorAll('script[type="application/ld+json"]').forEach((s) => s.remove())
    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.setAttribute('data-rseo-jsonld', '')
    script.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@graph': [...sitewideJsonLd, ...pageBlocks],
    })
    document.head.appendChild(script)
}

/**
 * Applies title, description, canonical, robots, Open Graph, Twitter and
 * JSON-LD for the active route.
 *
 * Rendered once inside the router rather than per route, so every page's head
 * is described in one place (content/seo.ts) and cannot drift between routes.
 * Canonicals are always the production origin, never /v2/, so a staged page
 * points search engines at the real URL.
 */
export default function RouteSeo() {
    const { pathname } = useLocation()

    useEffect(() => {
        const { seo, path } = resolve(pathname)
        const canonical = path === '/' ? `${PROD_ORIGIN}/` : `${PROD_ORIGIN}${path}`

        document.title = seo.title

        upsert('meta', { name: 'description', content: seo.description }, 'name')
        upsert('meta', {
            name: 'robots',
            content: seo.noindex ? 'noindex, follow' : 'index, follow',
        }, 'name')
        upsert('link', { rel: 'canonical', href: canonical }, 'rel')

        // Open Graph
        const og: Record<string, string> = {
            'og:type': path === '/fixing-dns-leak' ? 'article' : 'website',
            'og:site_name': 'dnsleaktest.org',
            'og:title': seo.title,
            'og:description': seo.description,
            'og:url': canonical,
            'og:image': OG_IMAGE,
        }
        for (const [property, content] of Object.entries(og)) {
            upsert('meta', { property, content }, 'property')
        }

        // Twitter
        const tw: Record<string, string> = {
            'twitter:card': 'summary_large_image',
            'twitter:title': seo.title,
            'twitter:description': seo.description,
            'twitter:image': OG_IMAGE,
        }
        for (const [name, content] of Object.entries(tw)) {
            upsert('meta', { name, content }, 'name')
        }

        emitJsonLd(seo.jsonLd ?? [])

        // A page whose structured data is too big for the entry bundle gets it
        // second: the head is already correct, and the graph is rewritten once
        // the chunk lands. `live` guards a navigation that beat the import.
        if (!seo.lazyJsonLd) return
        let live = true
        seo.lazyJsonLd()
            .then((extra) => { if (live) emitJsonLd([...(seo.jsonLd ?? []), ...extra]) })
            .catch(() => { /* structured data is an enhancement, not the page */ })
        return () => { live = false }
    }, [pathname])

    return null
}

export { KNOWN_PATHS }
